import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Shield,
  Plus,
  Users,
  KeyRound,
  Edit2,
  Trash2,
  UserPlus,
  Sparkles,
  Info,
  CheckCircle2,
  Layers,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import {
  getAllRolesAPI,
  getPermissionsCatalogAPI,
  deleteRoleAPI,
} from "../../../../Redux/ApiCalls/Dashboard/RoleApi";
import { getAllMembers } from "../../../../Redux/ApiCalls/Dashboard/MembersAPI";
import { StudioRole } from "../../../../Data/role.dto";
import AddEditRoleDrawer from "./AddEditRoleDrawer";
import AssignRoleModal from "./AssignRoleModal";
import AssignedMembersModal from "./AssignedMembersModal";
import PermissionGate from "../../../atoms/Utlis/PermissionGate";
import { usePermissions } from "../../../../hooks/usePermissions";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import MarketplaceEmptyState, {
  MarketplaceLoadingSpinner,
} from "../Marketplace/MarketplaceEmptyState";
import { device } from "../../../atoms/Utlis/media";
import { IMAGE_CDN } from "../../../../config/config";

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.85) 0%, rgba(206, 86, 176, 0.85) 100%)";

export const RoleManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { roles, catalog, loading, isRolesFetched } = useAppSelector(
    (state) => state.role
  );
  const { members, isMemberFetched } = useAppSelector((state) => state.member);
  const { isStudioOwner, hasPermission } = usePermissions();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState<StudioRole | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedRoleIdForAssign, setSelectedRoleIdForAssign] = useState<
    number | string | null
  >(null);
  const [selectedMemberIdForAssign, setSelectedMemberIdForAssign] = useState<
    number | string | null
  >(null);

  const [roleToDelete, setRoleToDelete] = useState<StudioRole | null>(null);
  const [deleteForce, setDeleteForce] = useState(true);

  // Load catalog, roles, and members
  useEffect(() => {
    if (!isRolesFetched) {
      dispatch(getAllRolesAPI());
    }
    dispatch(getPermissionsCatalogAPI());
    if (!isMemberFetched) {
      dispatch(getAllMembers());
    }
  }, [dispatch, isRolesFetched, isMemberFetched]);

  // Handle URL query parameters (e.g. ?assignMember=123 or ?new=true)
  useEffect(() => {
    const assignMemberParam = searchParams.get("assignMember");
    const assignRoleParam = searchParams.get("assignRole");
    const newRoleParam = searchParams.get("new");

    if (assignMemberParam) {
      setSelectedMemberIdForAssign(assignMemberParam);
      setIsAssignModalOpen(true);
      // Clean query params so it doesn't re-trigger on modal close
      searchParams.delete("assignMember");
      setSearchParams(searchParams, { replace: true });
    } else if (assignRoleParam) {
      setSelectedRoleIdForAssign(assignRoleParam);
      setIsAssignModalOpen(true);
      searchParams.delete("assignRole");
      setSearchParams(searchParams, { replace: true });
    } else if (newRoleParam === "true") {
      setRoleToEdit(null);
      setIsDrawerOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const totalCatalogKeysCount = React.useMemo(() => {
    let count = 0;
    catalog.forEach((g) => {
      count += g.permissions.length;
    });
    return count;
  }, [catalog]);

  const handleOpenCreate = () => {
    setRoleToEdit(null);
    setIsDrawerOpen(true);
  };

  const handleOpenEdit = (role: StudioRole) => {
    setRoleToEdit(role);
    setIsDrawerOpen(true);
  };

  const [isViewMembersModalOpen, setIsViewMembersModalOpen] = useState(false);
  const [roleToViewMembers, setRoleToViewMembers] = useState<StudioRole | null>(
    null
  );

  const handleOpenAssign = (roleId?: number | string) => {
    setSelectedRoleIdForAssign(roleId || null);
    setSelectedMemberIdForAssign(null);
    setIsAssignModalOpen(true);
  };

  const handleOpenViewMembers = (role: StudioRole) => {
    setRoleToViewMembers(role);
    setIsViewMembersModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;
    try {
      await dispatch(
        deleteRoleAPI({ id: roleToDelete.id, force: deleteForce })
      ).unwrap();
      showSuccessToast(`Role "${roleToDelete.name}" deleted successfully.`);
      setRoleToDelete(null);
      dispatch(getAllRolesAPI());
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to delete role");
    }
  };

  return (
    <PageContainer>
      {/* Hero Header */}
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Access Control
              </HeroBadge>
              <PageTitle className="linear-text">Roles & Access</PageTitle>
              <PageSubtitle>
                Configure studio roles, module boundaries, and permission keys.
              </PageSubtitle>
            </div>
          </HeroText>

          <HeaderActions>
            <PermissionGate permission="role_management.update">
              <SecondaryActionButton
                type="button"
                onClick={() => handleOpenAssign()}
              >
                <UserPlus size={16} />
                Assign Role
              </SecondaryActionButton>
            </PermissionGate>

            <PermissionGate permission="role_management.create">
              <AddButton type="button" onClick={handleOpenCreate}>
                <Plus size={18} strokeWidth={2.4} />
                Add Role
              </AddButton>
            </PermissionGate>
          </HeaderActions>
        </HeroContent>
      </Hero>

      {/* Stats Summary */}
      <StatsGrid>
        <StatCard>
          <StatIconWrap>
            <Shield size={18} color="#c94daa" />
          </StatIconWrap>
          <StatBody>
            <StatValue>{roles.length}</StatValue>
            <StatLabel>Custom Roles</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard>
          <StatIconWrap>
            <KeyRound size={18} color="#c94daa" />
          </StatIconWrap>
          <StatBody>
            <StatValue>{totalCatalogKeysCount || 55}</StatValue>
            <StatLabel>Total Permissions</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard>
          <StatIconWrap>
            <Layers size={18} color="#c94daa" />
          </StatIconWrap>
          <StatBody>
            <StatValue>{catalog.length || 15}</StatValue>
            <StatLabel>Available Modules</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard>
          <StatIconWrap>
            <Users size={18} color="#c94daa" />
          </StatIconWrap>
          <StatBody>
            <StatValue>{(members || []).length}</StatValue>
            <StatLabel>Team Members</StatLabel>
          </StatBody>
        </StatCard>
      </StatsGrid>

      {/* Main Roles Table Panel */}
      <Panel>
        <PanelHeader>
          <PanelTitleWrap>
            <PanelTitle>Roles</PanelTitle>
            <PanelBadge>{roles.length} roles defined</PanelBadge>
          </PanelTitleWrap>

          <PermissionGate permission="role_management.create">
            <NewRoleBtn type="button" onClick={handleOpenCreate}>
              <Plus size={15} />
              Create Role
            </NewRoleBtn>
          </PermissionGate>
        </PanelHeader>

        {loading.roles ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : roles.length === 0 ? (
          <MarketplaceEmptyState
            padding="48px 22px"
            icon={<Shield size={38} strokeWidth={1.5} />}
            title="No Studio Roles Created Yet"
            description="Create custom roles like Studio Manager, Lead Photographer, or Editor to grant granular module permissions to team members."
            actions={[
              {
                label: "Create First Role",
                onClick: handleOpenCreate,
                icon: <Plus size={15} />,
              },
            ]}
            hint={{
              icon: <Sparkles size={13} />,
              text: "Assigning a role auto-registers member login (role=4) with phone OTP",
            }}
          />
        ) : (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: "60px" }}>#</Th>
                  <Th>Role Name</Th>
                  <Th>Permissions</Th>
                  <Th>Assigned Members</Th>
                  <Th style={{ textAlign: "right", minWidth: "180px" }}>
                    Actions
                  </Th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role, idx) => {
                  const permCount = role.permissions?.length || 0;
                  const membersCount =
                    role.members_count ??
                    (role as any).member_count ??
                    (role.assigned_members ? role.assigned_members.length : 0);
                  const assignedMembers = role.assigned_members || [];

                  return (
                    <Tr key={role.id}>
                      <Td>
                        <IndexBadge>{idx + 1}</IndexBadge>
                      </Td>

                      <Td>
                        <RoleNameWrap>
                          <RoleNameText>{role.name}</RoleNameText>
                          {role.description && (
                            <RoleDescText>{role.description}</RoleDescText>
                          )}
                        </RoleNameWrap>
                      </Td>

                      <Td>
                        <PermTagWrap>
                          <PermBadge $count={permCount}>
                            <KeyRound size={12} />
                            {permCount} / {totalCatalogKeysCount || 55} keys
                          </PermBadge>
                        </PermTagWrap>
                      </Td>

                      <Td>
                        {membersCount === 0 ? (
                          <EmptyMembersCell>
                            <MembersCountText>
                              <Users size={14} color="#a3a3a3" />
                              <span>0 members</span>
                            </MembersCountText>
                          </EmptyMembersCell>
                        ) : (
                          <AssignedMembersCellWrap
                            onClick={() => handleOpenViewMembers(role)}
                            title="Click to view and manage assigned members"
                          >
                            <AvatarStack>
                              {assignedMembers.slice(0, 3).map((m, mIdx) => {
                                const avatarSrc = m.profile_image
                                  ? `${IMAGE_CDN}/${m.profile_image}`
                                  : "";
                                return (
                                  <StackedAvatarWrap
                                    key={m.id || mIdx}
                                    style={{ zIndex: 10 - mIdx }}
                                    title={m.name}
                                  >
                                    {avatarSrc ? (
                                      <StackedAvatar
                                        src={avatarSrc}
                                        alt={m.name}
                                      />
                                    ) : (
                                      <StackedAvatarFallback>
                                        {m.name?.charAt(0)?.toUpperCase() || "M"}
                                      </StackedAvatarFallback>
                                    )}
                                  </StackedAvatarWrap>
                                );
                              })}
                              {membersCount > 3 && (
                                <MoreMembersBadge>
                                  +{membersCount - 3}
                                </MoreMembersBadge>
                              )}
                            </AvatarStack>

                            <MembersDetailsWrap>
                              <MembersCountBadge>
                                <Users size={12} />
                                <span>
                                  <strong>{membersCount}</strong> member
                                  {membersCount !== 1 ? "s" : ""}
                                </span>
                              </MembersCountBadge>
                              {assignedMembers.length > 0 && (
                                <MemberNamesPreview>
                                  {assignedMembers
                                    .slice(0, 2)
                                    .map((m) => m.name)
                                    .join(", ")}
                                  {assignedMembers.length > 2
                                    ? ` +${assignedMembers.length - 2} more`
                                    : ""}
                                </MemberNamesPreview>
                              )}
                            </MembersDetailsWrap>
                          </AssignedMembersCellWrap>
                        )}
                      </Td>

                      <Td style={{ textAlign: "right" }}>
                        <ActionRow>
                          <PermissionGate permission="role_management.update">
                            <ActionButton
                              type="button"
                              onClick={() => handleOpenAssign(role.id)}
                              title="Assign this role to a team member"
                            >
                              <UserPlus size={14} />
                              <span>Assign</span>
                            </ActionButton>
                          </PermissionGate>

                          {membersCount > 0 && (
                            <IconActionBtn
                              type="button"
                              onClick={() => handleOpenViewMembers(role)}
                              title="View assigned members"
                            >
                              <Users size={15} />
                            </IconActionBtn>
                          )}

                          <PermissionGate permission="role_management.update">
                            <IconActionBtn
                              type="button"
                              onClick={() => handleOpenEdit(role)}
                              title="Edit role and permissions"
                            >
                              <Edit2 size={15} />
                            </IconActionBtn>
                          </PermissionGate>

                          <PermissionGate permission="role_management.delete">
                            <IconActionBtn
                              type="button"
                              $danger
                              onClick={() => setRoleToDelete(role)}
                              title="Delete this role"
                            >
                              <Trash2 size={15} />
                            </IconActionBtn>
                          </PermissionGate>
                        </ActionRow>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </Panel>

      {/* Add / Edit Role Drawer */}
      <AddEditRoleDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        roleToEdit={roleToEdit}
        catalog={catalog}
      />

      {/* Assign Role to Member Modal */}
      <AssignRoleModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        initialRoleId={selectedRoleIdForAssign}
        initialMemberId={selectedMemberIdForAssign}
        onAssignedSuccess={() => {
          dispatch(getAllRolesAPI());
        }}
      />

      {/* View & Manage Assigned Members Modal */}
      <AssignedMembersModal
        isOpen={isViewMembersModalOpen}
        onClose={() => setIsViewMembersModalOpen(false)}
        role={
          roles.find((r) => r.id === roleToViewMembers?.id) || roleToViewMembers
        }
        onOpenAssignModal={(roleId) => handleOpenAssign(roleId)}
      />

      {/* Delete Confirmation Modal */}
      {roleToDelete && (
        <DeleteModalOverlay onClick={() => setRoleToDelete(null)}>
          <DeleteModalContainer onClick={(e) => e.stopPropagation()}>
            <DeleteIconWrap>
              <AlertTriangle size={28} color="#ef4444" />
            </DeleteIconWrap>
            <DeleteTitle>Delete Role?</DeleteTitle>
            <DeleteMessage>
              Are you sure you want to delete the role{" "}
              <strong>&ldquo;{roleToDelete.name}&rdquo;</strong>? Members
              currently assigned this role will lose their custom permissions.
            </DeleteMessage>

            <ForceCheckboxWrap>
              <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={deleteForce}
                  onChange={(e) => setDeleteForce(e.target.checked)}
                  className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                />
                Force deletion even if team members are currently assigned
              </label>
            </ForceCheckboxWrap>

            <DeleteActionRow>
              <CancelDeleteBtn
                type="button"
                onClick={() => setRoleToDelete(null)}
                disabled={loading.delete}
              >
                Cancel
              </CancelDeleteBtn>
              <ConfirmDeleteBtn
                type="button"
                onClick={handleDeleteConfirm}
                disabled={loading.delete}
              >
                {loading.delete ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete Role"
                )}
              </ConfirmDeleteBtn>
            </DeleteActionRow>
          </DeleteModalContainer>
        </DeleteModalOverlay>
      )}
    </PageContainer>
  );
};

export default RoleManagementPage;

/* Styled Components */

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;

  @media ${device.tablet} {
    padding: 16px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 201, 81, 0.32) 0%,
    transparent 70%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SecondaryActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #ffffff;
  color: #171717;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fdf2f8;
    border-color: #c94daa;
    color: #c94daa;
  }
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(201, 77, 170, 0.28);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media ${device.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${device.mobileL} {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 18px 20px;
  border: 1px solid rgba(23, 23, 23, 0.07);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
  display: flex;
  align-items: center;
  gap: 14px;
`;

const StatIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fdf4fa;
  border: 1px solid rgba(201, 77, 170, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: #737373;
  margin-top: 2px;
`;

const Panel = styled.div`
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const PanelTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #171717;
  margin: 0;
`;

const PanelBadge = styled.span`
  font-size: 12px;
  color: #737373;
  background: #f5f5f5;
  padding: 3px 10px;
  border-radius: 9999px;
  font-weight: 500;
`;

const NewRoleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #262626;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f5f5f5;
    border-color: #d4d4d4;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 14px 24px;
  font-size: 12px;
  font-weight: 700;
  color: #737373;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const Tr = styled.tr`
  transition: background 0.15s;

  &:hover {
    background: #fafafa;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f5f5f5;
  }
`;

const Td = styled.td`
  padding: 16px 24px;
  font-size: 14px;
  color: #171717;
  vertical-align: middle;
`;

const IndexBadge = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #8c8c8c;
`;

const RoleNameWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const RoleNameText = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #171717;
`;

const RoleDescText = styled.span`
  font-size: 12px;
  color: #737373;
  margin-top: 2px;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PermTagWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PermBadge = styled.div<{ $count: number }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 9999px;
  background: #fdf2f8;
  color: #c94daa;
  border: 1px solid rgba(201, 77, 170, 0.2);
`;

const MembersCountText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #525252;

  strong {
    color: #171717;
  }
`;

const EmptyMembersCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const AssignedMembersCellWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 4px 8px 4px 4px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f5f5f5;
  }
`;

const AvatarStack = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const StackedAvatarWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #ffffff;
  margin-left: -8px;
  background: #f5f5f5;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;

  &:first-child {
    margin-left: 0;
  }
`;

const StackedAvatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StackedAvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  background: ${gradient};
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MoreMembersBadge = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  background: #f0f0f0;
  color: #525252;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -8px;
  z-index: 1;
`;

const MembersDetailsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MembersCountBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #171717;

  strong {
    color: #171717;
    font-weight: 700;
  }
`;

const MemberNamesPreview = styled.span`
  font-size: 11px;
  color: #737373;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  color: #262626;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f5f5f5;
    border-color: #c94daa;
    color: #c94daa;
  }
`;

const IconActionBtn = styled.button<{ $danger?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  color: ${(props) => (props.$danger ? "#ef4444" : "#525252")};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${(props) => (props.$danger ? "#fef2f2" : "#f5f5f5")};
    border-color: ${(props) => (props.$danger ? "#ef4444" : "#d4d4d4")};
    color: ${(props) => (props.$danger ? "#dc2626" : "#171717")};
  }
`;

/* Delete Modal */

const DeleteModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const DeleteModalContainer = styled.div`
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 18px;
  padding: 24px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const DeleteIconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const DeleteTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #171717;
  margin: 0 0 8px;
`;

const DeleteMessage = styled.p`
  font-size: 13px;
  color: #525252;
  line-height: 1.5;
  margin: 0 0 16px;

  strong {
    color: #171717;
  }
`;

const ForceCheckboxWrap = styled.div`
  margin-bottom: 20px;
  padding: 10px 14px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #f3f4f6;
  width: 100%;
  text-align: left;
`;

const DeleteActionRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const CancelDeleteBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  color: #525252;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
`;

const ConfirmDeleteBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  border-radius: 10px;
  border: none;
  background: #ef4444;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #dc2626;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
